/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Company} from 'types/entities/Company'
import {CompanyAddress} from 'types/entities/CompanyAddress'
import {CompanyContact} from 'types/entities/CompanyContact'
import {ThunkAction} from 'model/store/thunk'
import {
	deleteCompany,
	pushCompany,
	removeSelectedCompanyAddress,
	removeSelectedCompanyContact,
	setCompanies,
	setCompaniesTableMeta,
	setSelectedCompany,
	upsertSelectedCompanyAddress,
	upsertSelectedCompanyContact,
} from './CompaniesReducer'
import {parsePaginatedResponse} from 'utils/api/pagination'
import {CompaniesFilterType} from 'types/enums/CompaniesFilterType'
import RequestAction from 'store/RequestAction'
import {CsvImportMap} from 'components/XlsxImport/types'
import { XlsxImportResult } from 'types/import/XlsxImportResult'
import { parseImportResponse } from 'utils/import/parseImportResponse'
import ajax from 'helpers/ajax'
import { SELLER_API_KEY } from 'helpers/config'
import { consoleLog } from 'utils/utility'
import { MassActionRequest, MassActionResponse } from 'types/requests/MassActionRequest'
import { MassActionType } from 'types/enums/specific/MassAction'
import { sanitizeCompanyPayload } from 'utils/companies/sanitizeCompanyPayload'
import { MergeAuditLogEntry, setMergeAuditLog } from 'store/crm/CrmReducer'

export class CompaniesAction {
    static fetchMergeCompanies = (params: {
        main_company_id: number
        merged_company_ids: number[]
    }): ThunkAction<Promise<{ audit?: MergeAuditLogEntry }>> => {
        return async (dispatch, getState): Promise<{ audit?: MergeAuditLogEntry }> => {
            const mainCompanyId = Number(params.main_company_id)
            const mergedCompanyIds = Array.from(
                new Set(
                    params.merged_company_ids
                        .map((id) => Number(id))
                        .filter(
                            (id) =>
                                Number.isFinite(id) &&
                                id > 0 &&
                                id !== mainCompanyId
                        )
                )
            )

            if (!Number.isFinite(mainCompanyId) || mainCompanyId <= 0) {
                return Promise.reject(new Error('Invalid main company id'))
            }

            if (mergedCompanyIds.length === 0) {
                return Promise.reject(new Error('No companies selected to merge'))
            }

            const response: {
                audit?: MergeAuditLogEntry
                merge_audit?: MergeAuditLogEntry
                mergeAudit?: MergeAuditLogEntry
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/companies/merge',
                {
                    main_company_id: mainCompanyId,
                    merged_company_ids: mergedCompanyIds,
                },
                'post'
            )

            mergedCompanyIds.forEach((id) => dispatch(deleteCompany(id)))
            await dispatch(CompaniesAction.fetchCompanyById(mainCompanyId))

            const audit =
                response?.audit ?? response?.merge_audit ?? response?.mergeAudit
            return { audit }
        }
    }

    static fetchMergeAuditLog = (): ThunkAction<Promise<MergeAuditLogEntry[]>> => {
        return async (dispatch, getState): Promise<MergeAuditLogEntry[]> => {
            try {
                const response: {
                    audits?: MergeAuditLogEntry[]
                    merge_audits?: MergeAuditLogEntry[]
                    data?: MergeAuditLogEntry[]
                } = await RequestAction.makeAuthenticatedRequest(
                    getState,
                    '/admin/companies/merge-audit',
                    undefined,
                    'get'
                )
                const audits = Array.isArray(response?.audits)
                    ? response.audits
                    : Array.isArray(response?.merge_audits)
                      ? response.merge_audits
                      : Array.isArray(response?.data)
                        ? response.data
                        : []
                const normalized = audits.map((entry, index) => ({
                    id: String(entry.id ?? `merge_${index}`),
                    createdAt: String(entry.createdAt ?? (entry as { created_at?: string }).created_at ?? new Date().toISOString()),
                    mainCompanyId: Number(
                        entry.mainCompanyId ?? (entry as { main_company_id?: number }).main_company_id
                    ),
                    mainCompanyName:
                        entry.mainCompanyName ??
                        (entry as { main_company_name?: string }).main_company_name,
                    mergedCompanyIds: Array.isArray(entry.mergedCompanyIds)
                        ? entry.mergedCompanyIds.map(Number)
                        : Array.isArray((entry as { merged_company_ids?: number[] }).merged_company_ids)
                          ? ((entry as { merged_company_ids?: number[] }).merged_company_ids ?? []).map(Number)
                          : [],
                    mergedCompanyNames:
                        entry.mergedCompanyNames ??
                        (entry as { merged_company_names?: string[] }).merged_company_names,
                }))
                dispatch(setMergeAuditLog(normalized))
                return normalized
            } catch {
                return []
            }
        }
    }

    static fetchCompaniesTablePage = (params: {
        page: number
        per_page: number
        type?: CompaniesFilterType
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
    }): ThunkAction<
        Promise<{
            data: Company[]
            current_page: number
            per_page: number
            total: number
        }>
    > => {
        return async (
            dispatch,
            getState
        ): Promise<{
            data: Company[]
            current_page: number
            per_page: number
            total: number
        }> => {
            const url = `/admin/companies/table`

            const hasClientSorting = params.sorting && params.sorting.length > 0

            const effectiveSorting = hasClientSorting ? params.sorting : [{ id: 'id', desc: true }]

            const response: unknown = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    page: params.page,
                    per_page: params.per_page,
                    type: params.type && params.type !== 'all' ? params.type : undefined,
                    search: params.search,
                    sorting: effectiveSorting,
                },
                'post'
            )
            try {
                const parsedResponse = parsePaginatedResponse<Company>(response, 'companies')

                dispatch(setCompanies(parsedResponse.data))

                const sortByParam =
                    params.sort_by ??
                    (Array.isArray(effectiveSorting) && effectiveSorting.length > 0 ? effectiveSorting[0].id : null)

                const sortParam =
                    params.sort ??
                    (Array.isArray(effectiveSorting) && effectiveSorting.length > 0
                        ? effectiveSorting[0].desc
                            ? 'desc'
                            : 'asc'
                        : null)

                dispatch(
                    setCompaniesTableMeta({
                        currentPage: parsedResponse.current_page,
                        perPage: parsedResponse.per_page,
                        total: parsedResponse.total,
                        sortBy: sortByParam,
                        sort: sortParam,
                        search: params.search,
                    })
                )

                return parsedResponse
            } catch {
                return Promise.reject(response)
            }
        }
    }

    static fetchExportCompaniesXlsx = (params: {
        type?: CompaniesFilterType
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
        export?: 'xlsx' | 'csv' | 'pdf'
        [key: string]: any
    }): ThunkAction<Promise<string>> => {
        return async (_dispatch, getState): Promise<string> => {
			const url = `/admin/export/companies`

            const hasClientSorting = params.sorting && params.sorting.length > 0
            const effectiveSorting = hasClientSorting ? params.sorting : [{ id: 'id', desc: true }]

            const response: {url: string} = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                    page: params.page ?? 1,
                    per_page: params.per_page ?? 50,
                    type: params.type && params.type !== 'all' ? params.type : undefined,
                    search: params.search,
                    sorting: effectiveSorting,
                    export: params.export ?? 'xlsx',
                },
                'post',
                'json'
            )

            return response.url
        }
    }

    static fetchImportCompanies = (payload: CsvImportMap): ThunkAction<Promise<XlsxImportResult>> => {
        return async (dispatch, getState): Promise<XlsxImportResult> => {
            const url = '/admin/companies/import'
            const response: unknown = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    file: payload.file,
                    fileLocation: payload.fileLocation,
                    columns: payload.columns,
                    importOptions: payload.importOptions,
                    duplicateStrategy: payload.duplicateStrategy,
                },
                'post'
            )

            const data = (response as { companies?: Company[] }).companies ?? []
            data.forEach((c) => dispatch(pushCompany(c)))
            return parseImportResponse(response, data.length)
        }
    }

    static fetchCreateOrUpdateCompany = (companyData: Company): ThunkAction<Promise<Company>> => {
        return async (dispatch, getState): Promise<Company> => {
            const response: { company: Company } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/companies/create',
                sanitizeCompanyPayload(companyData)
            )

            const company: Company | undefined = response?.company

            if (company) {
                dispatch(pushCompany(company))
                dispatch(setSelectedCompany(company))
                return company
            }
            return Promise.reject(response)
        }
    }

    static fetchMassCreateOrUpdateCompany = (items: Partial<Company>[]): ThunkAction<Promise<Company[]>> => {
        return async (dispatch, getState): Promise<Company[]> => {
            const sanitizedItems = items.map((item) => sanitizeCompanyPayload(item))
            const ids = sanitizedItems
                .map((item) => Number(item.id))
                .filter((id) => Number.isFinite(id))
            const params = {
                items: sanitizedItems.map(({ id: _id, ...item }) => item),
            }

            const response: { companies?: Company[] } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/companies/mass-actions',
                {
                    ids,
                    action: MassActionType.Create,
                    params,
                },
                'post'
            )
            const companies: Company[] = response?.companies ?? []
            companies.forEach((company) => dispatch(pushCompany(company)))
            return companies
        }
    }

    static fetchMassAction = (
        payload: MassActionRequest
    ): ThunkAction<Promise<MassActionResponse>> => {
        return async (_dispatch, getState): Promise<MassActionResponse> => {
            return await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/companies/mass-actions',
                payload,
                'post'
            )
        }
    }

    static fetchDeleteCompany = (companyId: number): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: {
                success: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/delete/${companyId}`,
                undefined,
                'delete'
            )

            if (response?.success) {
                dispatch(deleteCompany(companyId))
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }

    static fetchCompanyById = (companyId: number): ThunkAction<Promise<Company>> => {
        return async (dispatch, getState): Promise<Company> => {
            const response: {
                company: Company
                watchdogWorking: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/detail/${companyId}`,
                undefined,
                'get'
            )

            if (response?.company) {
                dispatch(setSelectedCompany(response.company))
                return response.company
            }
            return Promise.reject(response)
        }
    }

    static fetchCompanyByIdRaw = (companyId: number): ThunkAction<Promise<Company>> => {
        return async (_dispatch, getState): Promise<Company> => {
            const response: {
                company: Company
                watchdogWorking: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/detail/${companyId}`,
                undefined,
                'get'
            )

            if (response?.company) {
                return response.company
            }
            return Promise.reject(response)
        }
    }

    static fetchCompanyBranches = (parentCompanyId: number): ThunkAction<Promise<Company[]>> => {
        return async (dispatch, getState): Promise<Company[]> => {
            const response: {
                success: boolean
                branches: Company[]
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/branches/${parentCompanyId}`,
                {}
            )

            if (response?.success && response?.branches) {
                return response.branches
            }
            return Promise.reject(response)
        }
    }

    static fetchCheckDuplicates = (searchCriteria: {
        name?: string
        registration_number?: string
        street_address?: string
        city?: string
        postal_code?: string
        email?: string
        phone?: string
    }): ThunkAction<Promise<Company[]>> => {
        return async (_dispatch, getState): Promise<Company[]> => {
            const payload: {
                name?: string
                registration_number?: string
                street_address?: string
                city?: string
                postal_code?: string
                email?: string
                phone?: string
            } = {}

            const addIfValid = (key: keyof typeof payload, value?: string) => {
                if (typeof value === 'string') {
                    const trimmed = value.trim()
                    if (trimmed.length > 0) {
                        payload[key] = trimmed
                    }
                }
            }

            addIfValid('name', searchCriteria.name)
            addIfValid('registration_number', searchCriteria.registration_number)
            addIfValid('street_address', searchCriteria.street_address)
            addIfValid('city', searchCriteria.city)
            addIfValid('postal_code', searchCriteria.postal_code)
            addIfValid('email', searchCriteria.email)
            addIfValid('phone', searchCriteria.phone)

            const response: { companies?: Company[]; duplicates?: Company[] } =
                await RequestAction.makeAuthenticatedRequest(
                    getState,
                    '/admin/companies/check-duplicates',
                    payload,
                    'post'
                )

            const duplicates = response?.companies ?? response?.duplicates ?? []

            return Array.isArray(duplicates) ? duplicates : []
        }
    }

    static fetchCheckContactDuplicates = (searchCriteria: {
        name?: string
        email?: string
        phone?: string
    }): ThunkAction<Promise<CompanyContact[]>> => {
        return async (_dispatch, getState): Promise<CompanyContact[]> => {
            const response: { company_contacts?: CompanyContact[]; contacts?: CompanyContact[] } =
                await RequestAction.makeAuthenticatedRequest(
                    getState,
                    '/admin/companies/company-contact-check-duplicates',
                    searchCriteria,
                    'post'
                )

            const duplicates = response?.company_contacts ?? response?.contacts ?? []

            return Array.isArray(duplicates) ? duplicates : []
        }
    }

    static fetchGetCompanyContactFromAutocomplete = (name: string): ThunkAction<Promise<CompanyContact[]>> => {
        return async (): Promise<CompanyContact[]> => {
            try {
                const response = await ajax.post('/admin/companies/company-contact-autocomplete', {
                    identifier: name,
                    api_key: SELLER_API_KEY,
                })
                if (response?.success) {
                    const contacts =
                        response.data?.company_contacts ?? response.company_contacts ?? response.contacts ?? []
                    return Promise.resolve(contacts)
                } else {
                    return Promise.reject(response)
                }
            } catch (err) {
                consoleLog('fetchGetCompanyContactFromAutocomplete - reject')
                consoleLog(err)
                return Promise.reject(err)
            }
        }
    }

    static fetchCreateOrUpdateAddress = (addressData: CompanyAddress): ThunkAction<Promise<CompanyAddress>> => {
        return async (dispatch, getState): Promise<CompanyAddress> => {
            const response: { companyAddress: CompanyAddress } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/companies/create-address',
                addressData
            )

            const companyAddress: CompanyAddress | undefined = response?.companyAddress

            if (companyAddress) {
                dispatch(
                    upsertSelectedCompanyAddress({
                        address: companyAddress,
                    })
                )
                return companyAddress
            }
            return Promise.reject(response)
        }
    }

    static fetchCreateOrUpdateContact = (contactData: CompanyContact): ThunkAction<Promise<CompanyContact>> => {
        return async (dispatch, getState): Promise<CompanyContact> => {
            const response: { companyContact: CompanyContact } = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/companies/create-contact',
                contactData
            )

            const companyContact: CompanyContact | undefined = response?.companyContact

            if (companyContact) {
                dispatch(
                    upsertSelectedCompanyContact({
                        contact: companyContact,
                    })
                )
                return companyContact
            }
            return Promise.reject(response)
        }
    }

    static fetchDeleteContact = (contactId: number): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: {
                success: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/delete-contact/${contactId}`,
                undefined,
                'delete'
            )

            if (response?.success) {
                dispatch(removeSelectedCompanyContact({ contactId }))
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }

    static fetchDeleteAddress = (addressId: number): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: {
                success: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/delete-address/${addressId}`,
                undefined,
                'delete'
            )

            if (response?.success) {
                dispatch(removeSelectedCompanyAddress({ addressId }))
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }

    static setSelectedCompany = (company: Company | null) => {
        return setSelectedCompany(company)
    }

    static resetSelectedCompany = () => {
        return setSelectedCompany(null)
    }

    static fetchCompanyContactById = (contactId: number): ThunkAction<Promise<CompanyContact>> => {
        return async (_dispatch, getState): Promise<CompanyContact> => {
            const response: {
                contact: CompanyContact
                success: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/contact-detail/${contactId}`,
                undefined,
                'get'
            )

            if (response?.contact) {
                return response.contact
            }
            return Promise.reject(response)
        }
    }

    static fetchCompanyAddressById = (addressId: number): ThunkAction<Promise<CompanyAddress>> => {
        return async (_dispatch, getState): Promise<CompanyAddress> => {
            const response: {
                address: CompanyAddress
                success: boolean
            } = await RequestAction.makeAuthenticatedRequest(
                getState,
                `/admin/companies/address-detail/${addressId}`,
                undefined,
                'get'
            )

            if (response?.address) {
                return response.address
            }
            return Promise.reject(response)
        }
    }
}
